import React, { useEffect, useState } from "react";
import {
  InputFieldText,
  FieldGroup,
  Field,
  SubmitButton,
  FormCard,
  InputFieldDate,
  InputFieldSelect,
} from "../../reusable";
import { FormPartInterface, SaveValueInterface } from "../../../interfaces";
import globalStyles from "../../../globals/pages.module.scss";

export const Person = (props: FormPartInterface & { populateData?: any }) => {
  const { saveValueFunct, isActive, assignGroup, populateData } = props;
  const [currentStep, setCurrentStep] = useState(0);
  const [postCurrentStep, setPostCurrentStep] = useState(0);
  const [stepData, setStepData] = useState<any>({
    title: "",
    firstname: "",
    lastname: "",
    dob: "",
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: boolean }>({});
  const [showErrorsStep1, setShowErrorsStep1] = useState(false);
  const [showErrorsStep2, setShowErrorsStep2] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);

  // console.log('PERSON DATA:' + JSON.stringify(stepData))

  useEffect(() => {
    if (populateData) {
      setStepData({
        title: populateData.title || "",
        firstname: populateData.firstname || "",
        lastname: populateData.lastname || "",
        dob: populateData.dob || "",
      });
    }
  }, [populateData]);

  // Remove automatic validation - only validate when user tries to proceed

  const validateForm = (updateState = false) => {
    const newErrors: { [key: string]: boolean } = {};
    let hasErrors = false;

    // Validate all fields
    if (!stepData.title?.trim()) {
      newErrors.title = true;
      hasErrors = true;
    }
    if (!stepData.firstname?.trim()) {
      newErrors.firstname = true;
      hasErrors = true;
    }
    if (!stepData.lastname?.trim()) {
      newErrors.lastname = true;
      hasErrors = true;
    }
    if (!stepData.dob || stepData.dob.length === 0) {
      newErrors.dob = true;
      hasErrors = true;
    }

    if (updateState) {
      setFormErrors(newErrors);
      setIsFormValid(!hasErrors);
    }
    return !hasErrors;
  };

  const preSaveValueFunct = (dataToSave: SaveValueInterface) => {
    const saved = saveValueFunct(dataToSave);
    setStepData((prevData: any) => ({
      ...prevData,
      [dataToSave.field]: dataToSave.value,
    }));

    // Don't trim date values, only trim string values
    if (dataToSave.field === "dob") {
      if (dataToSave.value && dataToSave.value.length > 0) {
        setFormErrors((prev) => ({
          ...prev,
          [dataToSave.field]: false,
        }));
      }
    } else if (
      dataToSave.value &&
      typeof dataToSave.value === "string" &&
      dataToSave.value.trim()
    ) {
      setFormErrors((prev) => ({
        ...prev,
        [dataToSave.field]: false,
      }));
    }
    return saved;
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    const isStep1Valid = !!(
      stepData.title?.trim() &&
      stepData.firstname?.trim() &&
      stepData.lastname?.trim()
    );
    const isStep2Valid = !!(stepData.dob && stepData.dob.length > 0);

    if (currentStep === 0) {
      setShowErrorsStep1(true);
      if (!isStep1Valid) return;
      setShowErrorsStep2(false);
      // Notify parent to hide hero title
      try {
        window.dispatchEvent(
          new CustomEvent("personStepChange", { detail: 1 })
        );
      } catch (_) {}
      setCurrentStep(1);
      // Scroll to top after step change
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setShowErrorsStep2(true);
    if (!isStep2Valid) return;

    Object.keys(stepData).forEach((field) => {
      saveValueFunct({ field, value: stepData[field] });
    });

    setTimeout(() => {
      assignGroup("contact");
    }, 400);
  };

  return isActive === "person" ? (
    <FieldGroup title="Basic Contact Information">
      <FormCard
        assignPostStep={setPostCurrentStep}
        step={currentStep}
        onBack={handleBack}
        isFirstStep={currentStep === 0}
      >
        {currentStep === 0 && (
          <>
            <Field
              fieldLabel="Title"
              labelPosition="left"
              enhanced={true}
              extraStyle={{ marginBottom: "20px" }}
            >
              <InputFieldSelect
                fieldName="title"
                saveValue={preSaveValueFunct}
                options={[
                  { value: "Mr", label: "Mr" },
                  { value: "Mrs", label: "Mrs" },
                  { value: "Miss", label: "Miss" },
                  { value: "Ms", label: "Ms" },
                  { value: "Dr", label: "Dr" },
                  { value: "Prof", label: "Prof" },
                  { value: "Other", label: "Other" },
                ]}
                required={true}
                showErrors={showErrorsStep1}
                enhanced={true}
              />
            </Field>
            <div
              className={`${globalStyles.nameFields} ${globalStyles.nameFields}`}
            >
              <Field
                fieldLabel="Firstname"
                labelPosition="left"
                extraStyle={{ flex: 1, margin: "0 0 20px" }}
                enhanced={true}
              >
                <InputFieldText
                  fieldName="firstname"
                  saveValue={preSaveValueFunct}
                  populateValue={stepData.firstname || ""}
                  enhanced={true}
                  required={true}
                  showErrors={showErrorsStep1}
                />
              </Field>
              <Field
                fieldLabel="Lastname"
                labelPosition="left"
                extraStyle={{ flex: 1, margin: "0 0 20px" }}
                enhanced={true}
              >
                <InputFieldText
                  fieldName="lastname"
                  saveValue={preSaveValueFunct}
                  populateValue={stepData.lastname || ""}
                  enhanced={true}
                  required={true}
                  showErrors={showErrorsStep1}
                />
              </Field>
            </div>
          </>
        )}

        {currentStep === 1 && (
          <Field
            fieldLabel="Date of birth"
            labelPosition="left"
            enhanced={true}
          >
            <InputFieldDate
              fieldName="dob"
              saveValue={preSaveValueFunct}
              populateValue={stepData.dob || ""}
              enhanced={true}
              required={true}
              showErrors={showErrorsStep2}
            />
          </Field>
        )}

        <SubmitButton
          text="Continue"
          btnType="primary"
          onClick={handleNext}
          disabled={
            !(currentStep === 0
              ? stepData.title?.trim() &&
                stepData.firstname?.trim() &&
                stepData.lastname?.trim()
              : stepData.dob && stepData.dob.length > 0)
          }
        />
      </FormCard>
    </FieldGroup>
  ) : (
    <></>
  );
};
